import { faComment } from '@fortawesome/pro-regular-svg-icons/faComment';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { QIcon, QStack, QText, useQToast } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import { formatConversationTimestamp } from './formatConversationTimestamp';

type ConversationRowProps = {
  id: string;
  title: string;
  lastUpdated: string;
  /** Called after the conversation is successfully opened, e.g. to close a containing dropdown. */
  onSelect?: () => void;
};

export const ConversationRow = ({ id, title, lastUpdated, onSelect }: ConversationRowProps) => {
  const { t } = useTranslation('assistant');
  const { error: errorToast } = useQToast();
  const timestamp = useMemo(() => formatConversationTimestamp(lastUpdated), [lastUpdated]);

  const { openConversation } = useAIShellContext();

  const handleOpenConversation = useCallback(async () => {
    try {
      await openConversation(id);
      onSelect?.();
    } catch (error) {
      errorToast({
        description: t('messages.error_loading_message_history'),
        autoHideDuration: 5000,
        isClosable: true,
      });
    }
  }, [openConversation, id, errorToast, t, onSelect]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handleOpenConversation();
    },
    [handleOpenConversation],
  );

  return (
    <QStack
      direction="row"
      role="button"
      spacing="8px"
      sx={{
        alignItems: 'center',
        cursor: 'pointer',
        justifyContent: 'space-between',
        paddingX: '12px',
        paddingY: '8px',
        width: '100%',
        '&:hover': {
          backgroundColor: 'surface.navigation.hover',
        },
      }}
      tabIndex={0}
      onClick={handleOpenConversation}
      onKeyDown={handleKeyDown}
    >
      <QStack direction="row" spacing="8px" sx={{ alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
        <QIcon
          icon={faComment}
          sx={{ fontSize: '12px', color: 'text.onSurface.background.mediumContrast', flexShrink: 0 }}
        />
        <QText sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{title}</QText>
      </QStack>
      <QText sx={{ color: 'text.onSurface.background.mediumContrast', flexShrink: 0 }}>{timestamp}</QText>
    </QStack>
  );
};
